// import CardToMuchData from 'components/card/CardToMuchData'
import { groupBy, isUndefined, round, keyBy } from 'lodash'
import PropTypes from 'prop-types'
import DtoAnalysis from 'quality/dto/analyse/DtoAnalysis'
import React, { useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
// import useBoolean from 'utils/customHook/useBoolean'
import { getDate } from '../../../utils/DateUtil'
import { exportFile, getDataWithHeaders } from '../../../utils/ExportDataUtil'
import useListIndexed from 'utils/customHook/useListIndexed'
import { CardTable } from '../../../components/datatable/NewTable'
import { Grid2 } from '@mui/material'
import useBoolean from 'utils/customHook/useBoolean'
import ExportFileModal from 'components/modal/ExportFileModal'
import useActions from 'utils/customHook/useActions'
import ExportAction from 'export/actions/ExportAction'

const stationTableHeaders = ['stationCode', 'cityCode', 'cityLabel', 'stationName', 'nbSample', 'nbSamplesNC', 'nbResults', 'nbResultsNC', 'nonClassifiedResults', 'complianceTx']
const sampleTableHeaders = ['stationCode', 'cityCode', 'cityLabel', 'stationName', 'sampleDate', 'nbResults', 'nbResultsNC', 'nonClassifiedResults', 'complianceTx']
const stationExportHeaders = ['stationCode', 'cityCode', 'cityLabel', 'stationName', 'nbSample', 'nbSamplesNC', 'nbResults', 'nbResultsNC', 'nonClassifiedResults', 'complianceTx']
const sampleExportHeaders = ['stationCode', 'cityCode', 'cityLabel', 'stationName', 'sampleDate', 'nbResults', 'nbResultsNC', 'nonClassifiedResults', 'complianceTx']
// const maxNbElements = 25000

// use displayAll if needed
const SamplePanel = ({
    analysis = [],
    researchCriterias = {},
}) => {
    const {
        qualitometers,
        units,
        contributors,
        qualifications,
        remarks,
        status,
        fractions,
        supports,
        typeEnvironmentModels,
        departments,
        citiesIndex,
        parametersIndex,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        units: store.UnitReducer.units,
        contributors: store.ContributorReducer.contributors,
        qualifications: store.QualityReducer.qualifications,
        remarks: store.OperationReducer.remarks,
        status: store.QualityReducer.status,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
        departments: store.HomeReducer.departments,
        citiesIndex: store.CityReducer.citiesIndex,
        parametersIndex: store.ParameterReducer.parametersIndex,
    }), shallowEqual)

    const dispatch = useDispatch()

    const {
        value: isExportModalOpen,
        setTrue: openExportModal,
        setFalse: closeExportModal,
    } = useBoolean(false)

    useActions(() => ({
        exportList: [{
            onClick: openExportModal,
            label: i18n.excel,
        }],
    }), [])

    const nbAllElement = analysis.length
    const qualitometersIndex = useMemo(() => keyBy(qualitometers, 'id'), [qualitometers])
    const contributorsIndex = useMemo(() => keyBy(contributors, 'id'), [contributors])
    const unitsIndex = useMemo(() => keyBy(units, 'id'), [units])
    const supportsIndex = useMemo(() => keyBy(supports, 'id'), [supports])
    const fractionsIndex = useMemo(() => keyBy(fractions, 'id'), [fractions])

    const exportTypes = useMemo(() => {
        const models = typeEnvironmentModels.map(model => ({
            name: model,
            formats: [{
                type: i18n.excelFile,
                callback: () => {
                    if (nbAllElement > 20000) {
                        dispatch(ExportAction.exportResearchModelWithTmpExport(researchCriterias, model))
                        closeExportModal()
                    } else {
                        dispatch(ExportAction.exportResearchModel(researchCriterias, model))
                        closeExportModal()
                    }
                },
            }],
        }))

        return [
            {
                name: i18n.resultsTable,
                formats: [{
                    type: i18n.excelFile,
                    callback: () => {
                        dispatch(ExportAction.exportResearchFullCSV(researchCriterias, 'xlsx'))
                        closeExportModal()
                    },
                }, {
                    type: i18n.csvFile,
                    callback: () => {
                        dispatch(ExportAction.exportResearchFullCSV(researchCriterias, 'csv'))
                        closeExportModal()
                    },
                }],
            },
            ...models,
        ]
    }, [citiesIndex, contributorsIndex, departments, dispatch, analysis, fractionsIndex, parametersIndex, qualifications, qualitometersIndex, remarks, researchCriterias, status, supportsIndex, typeEnvironmentModels, unitsIndex, nbAllElement])


    // const {
    //     value: displayAll,
    //     setTrue: forceDisplay,
    // } = useBoolean(false)

    const groupQualitometers = useListIndexed(qualitometers, 'id')

    const stationsData = useMemo(() => {
        const groupedByStations = groupBy(analysis, 'qualitometer')
        return Object.keys(groupedByStations).map(key => {
            const station = groupQualitometers[key]
            const city = station?.townCode ? citiesIndex[station.townCode] : null
            const cityLabelWithCode = city?.labelWithCode

            const listAnalysis = groupedByStations[key]
            const classifiedAnalysis = listAnalysis.filter(a => a.threshold)
            const groupedOperations = groupBy(listAnalysis, 'operation')
            const sampleIds = Object.keys(groupedOperations)
            const samplesNC = sampleIds.filter(id => groupedOperations[id].find(v => !isUndefined(v.threshold?.[`threshold${v.index}`])))
            const resultsNC = listAnalysis.filter(a => !isUndefined(a.threshold?.[`threshold${a.index}`]))
            const complianceTx = classifiedAnalysis.length ? `${round((classifiedAnalysis.length - resultsNC.length) * 100 / classifiedAnalysis.length, 2)} %` : '100 %'
            const color = complianceTx !== '100 %' ? 'red' : 'green'

            return {
                stationCode: { value: station?.code },
                city: { value: cityLabelWithCode },
                cityCode: { value: station?.townCode },
                cityLabel: { value: city?.name },
                stationName: { value: station?.name },
                nbSample: { value: sampleIds.length, format: '0', cellType: 'number' },
                nbSamplesNC: { value: samplesNC.length, format: '0', cellType: 'number' },
                nbResults: { value: listAnalysis.length, format: '0', cellType: 'number' },
                nbResultsNC: { value: resultsNC.length, format: '0', cellType: 'number' },
                nonClassifiedResults: { value: listAnalysis.length - classifiedAnalysis.length, format: '0', cellType: 'number' },
                complianceTx: { value: complianceTx, classNameColor: color, color, cellType: 'right' },
            }
        })
    }, [citiesIndex, analysis, groupQualitometers])

    const samplesData = useMemo(() => {
        const groupedBySamples = groupBy(analysis, a => `${a.qualitometer}#${a.operation}`)
        return Object.keys(groupedBySamples).map(key => {
            const [idQualito] = key.split('#')
            const listAnalysis = groupedBySamples[key]
            const station = groupQualitometers[idQualito]
            const city = station?.townCode ? citiesIndex[station.townCode] : null
            const cityLabelWithCode = city?.labelWithCode

            const classifiedAnalysis = listAnalysis.filter(a => a.threshold)
            const resultsNC = listAnalysis.filter(a => !isUndefined(a.threshold?.[`threshold${a.index}`]))
            const complianceTx = classifiedAnalysis.length ? `${round((classifiedAnalysis.length - resultsNC.length) * 100 / classifiedAnalysis.length, 2)} %` : '100 %'
            const tooltip = {
                setTooltip: () => resultsNC.length > 0 ? (
                    <div>
                        <span>
                            {`${i18n.overrunedParameters}: `}
                            <br />
                            {
                                resultsNC.map((a, i) => (
                                    <span key={i}>
                                        {`${parametersIndex[a.parameter]?.shortLabelWithCode}: ${a.value} - ${i18n.threshold}(${a.threshold?.[`threshold${a.index}`] ?? '?'})`}
                                        <br />
                                    </span>
                                ))
                            }
                        </span>
                    </div>
                ) : (
                    <div>
                        <span>
                            {i18n.noOverrun}
                            <br />
                        </span>
                    </div>
                ),
            }
            const color = complianceTx !== '100 %' ? 'red' : 'green'
            return {
                stationCode: { value: station?.code },
                city: { value: cityLabelWithCode },
                cityCode: { value: station?.townCode },
                cityLabel: { value: city?.name },
                stationName: { value: station?.name },
                sampleDate: { value: getDate(listAnalysis[0].sampleDate), format: 'dd/MM/yyyy', cellType: 'date', ...tooltip },
                nbResults: { value: listAnalysis.length, format: '0', cellType: 'number', ...tooltip },
                nbResultsNC: { value: resultsNC.length, format: '0', cellType: 'number', ...tooltip },
                nonClassifiedResults: { value: listAnalysis.length - classifiedAnalysis.length, format: '0', cellType: 'number', ...tooltip },
                complianceTx: { value: complianceTx, classNameColor: color, color, ...tooltip, cellType: 'right' },
                // samplePoint: { value: listAnalysis[0].samplePoint || '', ...tooltip },
            }
        })
    }, [citiesIndex, analysis, groupQualitometers, parametersIndex])

    return (
        <Grid2 container spacing={1}>
            {/* {
                (samplesData.length > maxNbElements || stationsData.length > maxNbElements) && !displayAll && (
                    <CardToMuchData
                        nbElem={samplesData.length}
                        forceDisplay={forceDisplay}
                        maxNbElements={maxNbElements}
                    />
                )
            } */}
            <Grid2 size={12}>
                <CardTable
                    title={i18n.stations}
                    rows={stationsData}
                    headers={stationTableHeaders}
                    actions={[{
                        icon: 'download',
                        tooltip: i18n.export,
                        onClick: () => {
                            exportFile({
                                data: getDataWithHeaders(stationsData, stationTableHeaders),
                                exportType: 'xlsx',
                                titleFile: `${i18n.research}-${i18n.quality}`,
                            })
                        },
                    }]}
                    data-cy={'station_table'}
                />
            </Grid2>
            <Grid2 size={12}>
                <CardTable
                    title={i18n.nbSample}
                    rows={samplesData}
                    headers={sampleTableHeaders}
                    actions={[{
                        icon: 'download',
                        tooltip: i18n.export,
                        onClick: () => {
                            exportFile({
                                data: getDataWithHeaders(samplesData, sampleTableHeaders),
                                exportType: 'xlsx',
                                titleFile: `${i18n.research}-${i18n.quality}`,
                            })
                        },
                    }]}
                    data-cy={'samples_table'}
                />
            </Grid2>
            <ExportFileModal
                open={isExportModalOpen}
                onClose={closeExportModal}
                data={exportTypes}
            />
        </Grid2>
    )
}

SamplePanel.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysis)),
    researchCriterias: PropTypes.shape({}),
}

export default SamplePanel